import { createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import { RootSelector } from 'src/creativeHub/models/rootSelector';
import {
  CHNodeIsFolder,
  getParent,
  NodeLabel,
  NodeLabelSchema,
  NodePathParams,
} from 'src/general/node';
import { getSelectedRowsInCurrentFolder } from '../fileManager/selectors/selectors';
import { z } from 'zod';
import Notifications from 'react-notification-system-redux';
import type { THelpers } from '@myua/rtk-helper';
import { api } from '../../../general/apiScenarios';

import { scenarioFactoryAsync } from 'src/general/scenarioFactoryAsync';
import { _take, ListenerApiSafe } from 'src/general/listenerMiddlewareAddons';
import { INode } from 'src/general/model';

export const PackerJobStatus = z.union([
  z.literal('pending'),
  z.object({ node_id: z.number() }),
  z.object({ error: z.string() }),
]);

// Single source of truth: platform titles by key
export const platformsWithTitles = {
  applovin: 'Applovin',
  mintegral: 'Mintegral',
  unity: 'Unity',
  google: 'Google',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  moloco: 'Moloco',
} as const;

// Derive supported platform keys from the titles map
export const supportedPlatforms = Object.keys(platformsWithTitles) as Array<
  keyof typeof platformsWithTitles
>;

export type Compatibility = keyof typeof platformsWithTitles;

export const PackerImportJobSchema = z.object({
  originalNode: NodeLabelSchema,
  status: PackerJobStatus,
  compatibility: z.enum(supportedPlatforms as [Compatibility, ...Compatibility[]]),
  taskID: z.string(),
});

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PackerImportJobSchema = z.infer<typeof PackerImportJobSchema>;

const PackerShape = z.record(z.string(), PackerImportJobSchema);

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PackerShape = z.infer<typeof PackerShape>;

const StoreSlice = z.object({
  packer: PackerShape,
});
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type StoreSlice = z.infer<typeof StoreSlice>;

export const PackerConvertResult = INode.merge(NodePathParams);

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type PackerConvertResult = z.infer<typeof PackerConvertResult>;

type ImportRequest = {
  compatibility: PackerImportJobSchema['compatibility'];
  node: NodeLabel;
  customName: string;
  appStoreURL: string;
  googlePlayStoreURL: string;
  project: number;
};

export const importRequested = createAction<
  { requests: ImportRequest[] },
  'general/packer/REQUESTED'
>('general/packer/REQUESTED');
export const taskSubmitted = createAction<
  Pick<PackerImportJobSchema, 'originalNode' | 'compatibility' | 'taskID'>,
  'general/packer/TASK_SUBMITTED'
>('general/packer/TASK_SUBMITTED');
export const taskUpdated = createAction<
  Pick<PackerImportJobSchema, 'taskID' | 'status'>,
  'general/packer/TASK_UPDATED'
>('general/packer/TASK_UPDATED');
export const taskDeleted = createAction<{ taskID: string }, 'general/packer/TASK_DELETED'>(
  'general/packer/TASK_DELETED',
);
export const taskDeletedAll = createAction<void, 'general/packer/TASK_DELETED_ALL'>(
  'general/packer/TASK_DELETED_ALL',
);
export const btnCancelClicked = createAction<void, 'general/packer/CANCEL'>(
  'general/packer/CANCEL',
);

export const getIsAvailable: RootSelector<boolean> = createSelector(
  [getSelectedRowsInCurrentFolder],
  (selectedRows) => {
    const allFolders = selectedRows.every(CHNodeIsFolder);

    return allFolders && selectedRows.length > 0;
  },
);

export const getPacker = createSelector(
  (x: any): StoreSlice => StoreSlice.parse(x),
  (state) => state.packer,
);
export const getPackerJobs = createSelector(getPacker, (state) => Object.values(state));

export const PackerReducer = createReducer<PackerShape>({}, (b) =>
  b

    .addCase(taskSubmitted, (s, a) => {
      s[a.payload.taskID] = { ...a.payload, status: 'pending' };
    })
    .addCase(taskUpdated, (s, a) => {
      const value = s[a.payload.taskID];
      if (value) {
        s[a.payload.taskID] = { ...value, status: a.payload.status };
      }
    })
    .addCase(taskDeleted, (s, a) => {
      delete s[a.payload.taskID];
    })
    .addCase(taskDeletedAll, () => {
      return {};
    }),
);

export function packerRootListener(lm: THelpers<StoreSlice>) {
  console.debug('packerRootListener');
  const scenario = scenarioFactoryAsync(
    async (lapi: ListenerApiSafe<StoreSlice>, requests: ImportRequest[]) => {
      await Promise.all(
        requests.flatMap(async (request) => {
          const result = await api.crhub.playables.convert({
            source: request.node.id,
            social_network: request.compatibility,
            apple_store_url: request.appStoreURL,
            filename: request.customName,
            google_store_url: request.googlePlayStoreURL,
            project: request.project,
          });
          if (!result.success) {
            lapi.dispatch(
              Notifications.error({
                title: 'Failed to submit task for node ' + request.node.name,
                message: JSON.stringify(result.errorData?.response),
              }),
            );
            return;
          }
          const taskID = result.data.response.task_id;
          lapi.dispatch(
            taskSubmitted({
              originalNode: request.node,
              taskID,
              compatibility: request.compatibility,
            }),
          );

          try {
            const taskResult = await api.core.task<any>(taskID, lapi.signal);
            // if task succeeded
            if (typeof taskResult === 'string') {
              throw taskResult;
            }
            const packerResult = PackerConvertResult.parse(taskResult);

            lapi.signal.throwIfAborted();
            lapi.dispatch(
              taskUpdated({
                taskID,
                status: { node_id: getParent(packerResult).id },
              }),
            );
          } catch (e) {
            // if task failed
            lapi.dispatch(
              taskUpdated({
                taskID,
                status: { error: String(e) },
              }),
            );
          }
        }),
      );
    },
    async (lapi: ListenerApiSafe<StoreSlice>) => {
      await _take(lapi, btnCancelClicked);
      lapi.dispatch(taskDeletedAll()); // reset creativeUploader
    },
    () => {},
    'Packer',
  );

  lm.takeEvery(importRequested, async (action, lapi) => {
    console.debug('packerRootListener - importRequested');
    await scenario(lapi, action.payload.requests);
  });
}
